import React from 'react';
import { motion } from 'framer-motion';
import { Award, GraduationCap, Heart } from 'lucide-react';

const About = () => {
  return (
    <section id="about" className="section-padding bg-white">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            About the Project
          </h2>
          
          <div className="bg-gradient-to-br from-oralytics-50 to-oralytics-100 rounded-3xl p-8 md:p-12 shadow-xl">
            {/* Profile Section */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8 mb-8">
              {/* Profile Image */}
              <img 
                src="/images/team/founder-headshot.jpg" 
                alt="Ritvik Bansal - Founder" 
                className="w-32 h-32 rounded-full object-cover shadow-lg"
              />
              
              <div className="flex-1 text-left">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Meet Ritvik Bansal
                </h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Oralytics was developed by Ritvik Bansal, a student researcher from North Creek High School 
                  and the University of Düsseldorf. After watching family members suffer from late-stage 
                  periodontitis, Ritvik built a fully-integrated AI detection system using original hardware, 
                  deep learning models, and a real-time mobile app to enable early detection from anywhere.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <GraduationCap className="w-4 h-4" />
                    <span>North Creek High School</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <GraduationCap className="w-4 h-4" />
                    <span>University of Düsseldorf</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Motivation */}
            <div className="bg-white rounded-2xl p-6 mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <Heart className="w-6 h-6 text-red-500" />
                <h4 className="text-xl font-semibold text-gray-900">The Motivation</h4>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "Watching family members struggle with advanced periodontal disease made me realize 
                that early detection could have prevented years of pain and expensive treatments. 
                I wanted to create a solution that could bring professional-grade dental analysis 
                to everyone's smartphone."
              </p>
            </div>

            {/* Achievements */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Award className="w-6 h-6 text-oralytics-500" />
                  <h4 className="text-lg font-semibold text-gray-900">Research Recognition</h4>
                </div>
                <p className="text-gray-700 text-sm">
                  Presented research at the Washington State Science and Engineering Fair (WSSEF),
                  an ISEF-recognized organization, and received mentorship from leading researchers in computer vision.
                </p>
              </div>
              
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Award className="w-6 h-6 text-oralytics-500" />
                  <h4 className="text-lg font-semibold text-gray-900">Technical Innovation</h4>
                </div>
                <p className="text-gray-700 text-sm">
                  Developed custom 405nm UV hardware, trained clinical-grade AI models, 
                  and built a complete mobile application from concept to deployment.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About; 